/**
 * Authentication actions - signIn and signOut
 * Separated from auth.tsx to enable Fast Refresh for React components
 */

import { env } from "@/config/env";

/**
 * Get the base URL for auth endpoints.
 * In preview environments, this is the BFF Cloud Function URL so that
 * auth requests bypass Firebase Hosting (which strips cookies).
 * In dev/qa/prod, this is empty (relative URLs go through the LB).
 */
export function authUrl(path: string): string {
  const base = (env("AUTH_URL") ?? "").replace(/\/+$/, "");
  return `${base}${path}`;
}

/**
 * Sign in with a provider
 * Uses POST with CSRF token as required by Auth.js
 */
export async function signIn(
  provider?: string,
  options?: { callbackUrl?: string },
) {
  const callbackUrl = options?.callbackUrl ?? window.location.href;

  if (!provider) {
    // No provider specified - redirect to signin page to show provider list
    const params = new URLSearchParams({ callbackUrl });
    window.location.href = authUrl(`/api/auth/signin?${params.toString()}`);
    return;
  }

  try {
    // Get CSRF token first (credentials: include ensures cookie is set)
    const csrfResponse = await fetch(authUrl("/api/auth/csrf"), {
      credentials: "include",
    });
    const { csrfToken } = await csrfResponse.json();

    // Create and submit a form to POST to the signin endpoint
    // This is how Auth.js expects OAuth to be initiated
    const form = document.createElement("form");
    form.method = "POST";
    form.action = authUrl(`/api/auth/signin/${provider}`);

    // Add CSRF token
    const csrfInput = document.createElement("input");
    csrfInput.type = "hidden";
    csrfInput.name = "csrfToken";
    csrfInput.value = csrfToken;
    form.appendChild(csrfInput);

    // Add callback URL
    const callbackInput = document.createElement("input");
    callbackInput.type = "hidden";
    callbackInput.name = "callbackUrl";
    callbackInput.value = callbackUrl;
    form.appendChild(callbackInput);

    // Submit the form
    document.body.appendChild(form);
    form.submit();
  } catch (error) {
    console.error("[Auth] signIn error:", error);
    // Fallback: redirect to signin page
    window.location.href = authUrl("/api/auth/signin");
  }
}

/**
 * Sign out the current user
 */
export async function signOut(options?: { callbackUrl?: string }) {
  try {
    // Get CSRF token
    const csrfResponse = await fetch(authUrl("/api/auth/csrf"), {
      credentials: "include",
    });
    const { csrfToken } = await csrfResponse.json();

    // Post to signout endpoint
    const response = await fetch(authUrl("/api/auth/signout"), {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({
        csrfToken,
        callbackUrl: options?.callbackUrl ?? window.location.origin,
      }),
    });

    if (response.redirected) {
      window.location.href = response.url;
    } else {
      window.location.href = options?.callbackUrl ?? "/";
    }
  } catch {
    // Fallback: redirect to signout page
    window.location.href = authUrl("/api/auth/signout");
  }
}
